<script setup>
import { ref, computed, watch, onMounted, nextTick, useSlots } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  conversationLabels: {
    type: Array,
    required: true,
  },
  issue: {
    type: Object,
    default: null,
  },
  alwaysShowAll: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();
const accountLabels = useMapGetter('labels/getLabels');

const activeLabels = computed(() => {
  return accountLabels.value.filter(({ title }) =>
    props.conversationLabels.includes(title)
  );
});

const fallbackLabels = computed(() => {
  const src = props.conversationLabels || [];
  const hasAccountLabels =
    Array.isArray(accountLabels.value) && accountLabels.value.length > 0;
  if (hasAccountLabels || src.length === 0) {
    return [];
  }

  return src.map((title, index) => ({
    id: `fallback-${index}-${title}`,
    title,
    description: '',
    // Neutral fallback color until labels store loads
    color: '#64748b',
  }));
});

const displayLabels = computed(() => {
  return activeLabels.value.length > 0
    ? activeLabels.value
    : fallbackLabels.value;
});

const issueLabel = computed(() => {
  const issue = props.issue;
  if (!issue || !issue.name) {
    return null;
  }

  return {
    title: issue.name,
    description: issue.description || '',
    color: issue.color || '#00c4ad',
  };
});

const showAllLabels = ref(props.alwaysShowAll);
const showExpandLabelButton = ref(false);
const labelPosition = ref(-1);
const labelContainer = ref(null);

const computeVisibleLabelPosition = () => {
  if (props.alwaysShowAll) {
    showExpandLabelButton.value = false;
    labelPosition.value = displayLabels.value.length - 1;
    return;
  }
  const beforeSlot = (slots.before ? 100 : 0) + (issueLabel.value ? 100 : 0);
  if (!labelContainer.value) {
    return;
  }

  const labels = Array.from(labelContainer.value.querySelectorAll('.label'));
  let labelOffset = 0;
  showExpandLabelButton.value = false;
  labels.forEach((label, index) => {
    labelOffset += label.offsetWidth + 8;

    if (labelOffset < labelContainer.value.clientWidth - beforeSlot) {
      labelPosition.value = index;
    } else {
      showExpandLabelButton.value = labels.length > 1;
    }
  });
};

watch([activeLabels, issueLabel], () => {
  nextTick(() => computeVisibleLabelPosition());
});

onMounted(() => {
  computeVisibleLabelPosition();
});

const onShowLabels = e => {
  e.stopPropagation();
  showAllLabels.value = !showAllLabels.value;
  nextTick(() => computeVisibleLabelPosition());
};
</script>

<template>
  <div ref="labelContainer" v-resize="computeVisibleLabelPosition">
    <div
      v-if="displayLabels.length || $slots.before || (issue && issue.name)"
      class="flex items-end flex-shrink min-w-0 gap-y-1"
      :class="{
        'h-auto overflow-visible flex-row flex-wrap':
          showAllLabels || alwaysShowAll,
      }"
    >
      <slot name="before" />
      <woot-label
        v-if="issue && issue.name"
        :title="issue.name"
        :description="issue.description || ''"
        :color="issue.color || '#dc2626'"
        variant="smooth"
        class="!mb-0 max-w-[calc(100%-0.5rem)]"
        small
      />
      <woot-label
        v-for="(label, index) in displayLabels"
        :key="label ? label.id : index"
        :title="label.title"
        :description="label.description"
        :color="label.color"
        variant="smooth"
        class="!mb-0 max-w-[calc(100%-0.5rem)]"
        small
        :class="{
          'invisible absolute':
            !alwaysShowAll && !showAllLabels && index > labelPosition,
        }"
      />
      <button
        v-if="showExpandLabelButton && !alwaysShowAll"
        :title="
          showAllLabels
            ? $t('CONVERSATION.CARD.HIDE_LABELS')
            : $t('CONVERSATION.CARD.SHOW_LABELS')
        "
        class="h-5 py-0 px-1 flex-shrink-0 mr-6 ml-0 rtl:ml-6 rtl:mr-0 rtl:rotate-180 text-n-slate-11 border-n-strong dark:border-n-strong"
        @click="onShowLabels"
      >
        <fluent-icon
          :icon="showAllLabels ? 'chevron-left' : 'chevron-right'"
          size="12"
        />
      </button>
    </div>
  </div>
</template>
